import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ShoppingCart, X, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";

export function Header() {
  const { t, lang, toggleLang, cartCount, setCartOpen } = useApp();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/flotte", label: t.nav.fleet },
    { to: "/catalogue", label: t.nav.catalogue },
    { to: "/devis", label: t.nav.devis },
    { to: "/contact", label: t.nav.contact },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive(l.to) ? "bg-slate-100 text-cea-navy" : "text-slate-600 hover:text-cea-navy"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-bold text-cea-navy transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-cea-accent"
            aria-label="Change language"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang === "fr" ? "FR" : "EN"}
          </button>

          <button
            onClick={() => setCartOpen(true)}
            data-testid="cart-button"
            className="relative rounded-full p-2.5 text-cea-navy transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-cea-accent"
            aria-label="Cart"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-cea-accent px-1 text-[11px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="rounded-full p-2.5 text-cea-navy hover:bg-slate-100 lg:hidden" data-testid="mobile-menu-button" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mb-8 mt-2"><Logo /></div>
              <nav className="flex flex-col gap-1">
                {links.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={`rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                      isActive(l.to) ? "bg-slate-100 text-cea-navy" : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {l.label}
                  </Link>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
